import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const log = createLogger('FileUpload');
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { uploadCatalogImage } from '@/lib/api/upload';

interface FileUploadProps {
  value?: string;
  onChange: (url: string) => void;
  onError?: (error: string) => void;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
  label?: string;
  placeholder?: string;
  category: 'accessories' | 'devices';
}

export function FileUpload({
  value,
  onChange,
  onError,
  accept = 'image/*',
  maxSize = 5,
  className,
  label = 'Image',
  placeholder = 'Upload an image',
  category,
}: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      onError?.(`File size must be less than ${maxSize}MB`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onError?.('Please select an image file');
      return;
    }

    setIsUploading(true);
    setPreview(URL.createObjectURL(file));

    try {
      const imageUrl = await uploadCatalogImage(file, category);
      onChange(imageUrl);
    } catch (error) {
      log.error('Upload error', error);
      onError?.('Failed to upload image. Please try again.');
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemove = () => {
    onChange('');
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label>{label}</Label>

      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          'hover:border-primary/50 hover:bg-primary/5',
          isUploading && 'opacity-50 cursor-not-allowed',
          preview && 'border-primary bg-primary/5'
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isUploading}
        />

        {preview ? (
          <div className="space-y-4">
            <div className="relative inline-block">
              <img
                src={preview}
                alt="Preview"
                className="max-h-32 max-w-32 object-cover rounded-lg"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Cliquer pour changer l'image
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {isUploading ? (
              <div className="space-y-2">
                <Upload className="h-8 w-8 mx-auto animate-pulse" />
                <p className="text-sm">Chargement en cours...</p>
              </div>
            ) : (
              <div className="space-y-2">
                <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{placeholder}</p>
                  <p className="text-xs text-muted-foreground">
                    Glisser et déposer ou cliquer pour sélectionner
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Taille max: {maxSize}MB
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
